import { VisibilityElement } from '@/components/custom-elements/visibility'

/**
 * MODAL CUSTOM ELEMENT
 * Handled as a custom element due to their being next to no state to manage, and due to how neatly it interacts with Tailwind
 *
 * For set up:
 *   1. Ensure you wrap your modal elements with <x-modal>.
 *   2. Pass a 'data-modal-id' if you want intend to nest modals.
 *   3. Provide any buttons you want to modify modal state as a child, using "data-modal-role" with open, close, or toggle.
 *   4. To show or hide elements based on modal state, set a tailwind group and use "group-data-visible:" for stateful styling.
 *   5. Optional: Add 'data-hide-on-blur="true"' to hide the modal when it loses focus.
 *
 */

export class ModalElement extends VisibilityElement {
  // Child role selectors
  static OPEN_SELECTOR = '[data-modal-role~="open"]'
  static CLOSE_SELECTOR = '[data-modal-role~="close"]'
  static TOGGLE_SELECTOR = '[data-modal-role~="toggle"]'
  static DISABLE_SELECTOR = '[data-modal-role~="disable"]'

  openElement: NodeListOf<Element> | undefined
  closeElement: NodeListOf<Element> | undefined
  toggleElement: NodeListOf<Element> | undefined
  disableElement: NodeListOf<Element> | undefined
  hideOnBlur: boolean = false

  parseIdSelector() {
    const id = this.dataset.id
    return id ? `[data-modal-parent~="${id}"]` : ''
  }

  fetchChildren() {
    const idSelector = this.parseIdSelector()
    const targetWrapper = idSelector ? document : this
    this.openElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.OPEN_SELECTOR}`)
    this.closeElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.CLOSE_SELECTOR}`)
    this.toggleElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.TOGGLE_SELECTOR}`)
    this.disableElement = targetWrapper.querySelectorAll(`${idSelector}${ModalElement.DISABLE_SELECTOR}`)
  }

  parseConfig() {
    // Parse hideOnBlur from the data attribute
    this.hideOnBlur = this.dataset.hideOnBlur === 'true'
  }

  addEventListeners() {
    super.addEventListeners()
    this.openElement?.forEach((el) => el?.addEventListener('click', this.handleShow))
    this.closeElement?.forEach((el) => el?.addEventListener('click', this.handleHide))
    this.toggleElement?.forEach((el) => el?.addEventListener('click', this.handleToggle))
    this.disableElement?.forEach((el) => el?.addEventListener('click', this.handleDisable))

    // Add blur event listener if hideOnBlur is enabled
    if (this.hideOnBlur) {
      document.addEventListener('click', this.handleDocumentFocus)
    }
  }

  handleDocumentFocus = (event: FocusEvent) => {
    if (!this.contains(event.target as Node)) {
      this.handleHide()
    }
  }

  removeEventListeners() {
    super.removeEventListeners()
    this.openElement?.forEach((el) => el?.removeEventListener('click', this.handleShow))
    this.closeElement?.forEach((el) => el?.removeEventListener('click', this.handleHide))
    this.toggleElement?.forEach((el) => el?.removeEventListener('click', this.handleToggle))
    this.disableElement?.forEach((el) => el?.removeEventListener('click', this.handleDisable))

    // Remove blur event listener if it was added
    if (this.hideOnBlur) {
      document.removeEventListener('click', this.handleDocumentFocus)
    }
  }

  handleURLAnchor() {
    const anchorId = this.id
    if (anchorId) {
      const anchor = document.getElementById(anchorId)
      if (anchor) {
        this.handleShow()
      }
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.parseConfig()
    this.fetchChildren()
    this.addEventListeners()
    this.handleURLAnchor()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }
}
